import React from 'react';
import Portal from './../common/Portal';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { KeyCode } from '../util/Constants';
import StringUtil from '../util/StringUtil';
import { ThemeSetup } from '../themes/ThemeSetup';

import './AutocompleteField.css';

class AutocompleteField extends React.Component {
  currentKeyPressed = null;
  static propTypes = {
    data: PropTypes.array,
    selectedValues: PropTypes.array,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    allowFreehand: PropTypes.bool,
    showSelectedItemsInInputBox: PropTypes.bool,
    onChange: PropTypes.func,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
    validate: PropTypes.func
  };

  static defaultProps = {
    allowFreehand: false,
    showSelectedItemsInInputBox: true,
    placeholder: '',
    validate: () => true
  };

  _cache = {};

  state = {
    showList: false,
    selectedIndex: 0
  };

  componentWillReceiveProps(nextProps) {
    const val = this.props.value,
      nextVal = nextProps.value;

    if (val === nextVal) {
      return;
    }

    if (nextVal.length > 0 && !this.state.showList) {
      this.showList();
    } else if (nextVal.length === 0 && this.state.showList) {
      this.hideList();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedIndex !== this.state.selectedIndex) {
      const activeNode = document.querySelector('[name="AutocompleteField_List"] li.active');
      if (activeNode && activeNode.scrollIntoViewIfNeeded) {
        activeNode.scrollIntoViewIfNeeded();
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  render() {
    const { selectedValues, value, placeholder, showSelectedItemsInInputBox } = this.props;

    const cls = `wrapper ${selectedValues.length && showSelectedItemsInInputBox ? 'not-empty' : ''}`;
    const standaloneCls = `standalone-selected-values ${selectedValues.length ? 'not-empty' : ''}`;
    const selectedValuesMarkup = selectedValues.map(this.renderValue);

    return (
      <div name="AutocompleteField">
        {!showSelectedItemsInInputBox ? (
          <div className={standaloneCls} ref={(node) => (this._listNode = node)}>
            {selectedValuesMarkup}
          </div>
        ) : null}
        <div className={cls} onClick={() => this._input.focus()}>
          {showSelectedItemsInInputBox ? selectedValuesMarkup : null}
          <input
            ref={(node) => (this._input = node)}
            type="text"
            className={showSelectedItemsInInputBox ? 'full-width' : ''}
            value={value}
            placeholder={placeholder}
            onChange={this.onChange}
            onKeyDown={this.onKeyDown}
            onKeyUp={this.onKeyUp}
            onBlur={this.onBlur}
          />
        </div>
        {this.renderList()}
      </div>
    );
  }

  renderValue = (value, i) => {
    const { data, onRemove } = this.props;

    let label, icon;
    let item = this._cache[value];
    if (item) {
      label = item.label;
      icon = item.icon;
    } else if (typeof data[0] === 'object') {
      item = _.find(data, { value });
      if (!item) {
        return null;
      }
      label = item.label;
      icon = item.icon;
    } else {
      label = value;
      icon = null;
    }

    return (
      <div key={i} className={`selected-value ${icon ? '' : 'no-icon'}`}>
        {icon ? <img src={icon} /> : null}
        <span>{label}</span>
        <button onClick={() => onRemove(value)}>
          <img src={ThemeSetup.getImageURL('cross.svg')} />
        </button>
      </div>
    );
  };

  renderList = () => {
    const { showList, selectedIndex } = this.state;
    if (!this._input) {
      return null;
    }

    const rect = this._input.getBoundingClientRect();
    const style = {
      top: `${rect.bottom + 5}px`,
      left: `${rect.left}px`,
      maxWidth: '30%',
      wordBreak: 'break-word',
      width: '30%'
    };

    const list = this.getFilteredData().map((obj, i) => (
      <li key={i} className={i === selectedIndex ? 'active' : ''} onClick={() => this.onAdd(obj.value)}>
        {obj.icon ? <img src={obj.icon} /> : null}
        <span>{obj.label}</span>
      </li>
    ));
    if (showList && list.length) {
      return (
        <Portal defaultOpen={true} closeOnOutsideClick closeOnEsc>
          <ul name="AutocompleteField_List" style={style}>
            {list}
          </ul>
        </Portal>
      );
    } else {
      return null;
    }
  };

  getFilteredData = () => {
    const { data, value, selectedValues } = this.props;
    return data.filter((obj) => {
      switch (typeof obj) {
        case 'object':
          return !_.includes(selectedValues, obj.value) && StringUtil.contains(obj.label, value);
        case 'string':
          return !_.includes(selectedValues, obj) && StringUtil.contains(obj, value);
        default:
          return true;
      }
    });
  };

  showList = () => {
    this.setState({ showList: true });
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  };

  hideList = () => {
    this.setState({ showList: false });
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  };

  onAdd = (id, refocus = true) => {
    const { data, onAdd } = this.props;
    this.hideList();
    this._cache[id] = _.find(data, { value: id }) || { value: id, label: id };
    onAdd(id);
    if (refocus) {
      this._input.focus();
    }
  };

  onKeyDown = (e: KeyboardEvent) => {
    const { value, selectedValues, showSelectedItemsInInputBox, onRemove } = this.props;
    this.currentKeyPressed = e.keyCode;
    if (e.keyCode === KeyCode.DELETE && value === '' && selectedValues.length && showSelectedItemsInInputBox) {
      const item = selectedValues[selectedValues.length - 1];
      if (item) {
        onRemove(item.value || item);
      }
    }
  };

  onKeyUp = (e) => {
    const { allowFreehand, validate, value } = this.props;

    if (e.keyCode === KeyCode.ENTER && allowFreehand && validate(value)) {
      this.onAdd(value);
    }
  };

  onChange = (e) => {
    const { data, validate, onChange } = this.props;
    const { value } = e.target;
    const len = value.length;
    const email = value.substring(0, len - 1);
    // If the last character typed is a space or comma then add
    // the value to the selected list. Otherwise do a normal onChange.
    if (/[\s,]/.test(value[len - 1]) && validate(email) && this.currentKeyPressed !== KeyCode.BACKSPACE) {
      const found = _.find(data, { label: email });
      this.onAdd(found ? found.value : email);
    } else {
      onChange(e);
    }
  };

  onBlur = () => {
    const { allowFreehand, validate, value } = this.props;

    if (allowFreehand && value && validate(value)) {
      this.onAdd(value, false);
    }
  };

  onDocumentClick = (e) => {
    let node = e.target,
      nodes = [node];

    while (node.parentNode) {
      node = node.parentNode;
      nodes.push(node);
    }

    // Hide the list if somewhere on the page outside of this component was clicked.
    if (!_.includes(nodes, findDOMNode(this))) {
      this.hideList();
    }
  };

  onDocumentKeyDown = (e) => {
    const { keyCode } = e;

    if (keyCode === KeyCode.ENTER) {
      const selected = this.getFilteredData()[this.state.selectedIndex];
      if (!selected) return;
      this.setState({ selectedIndex: 0 });
      return this.onAdd(selected.value);
    }

    if (!_.includes([KeyCode.UP, KeyCode.DOWN], keyCode)) {
      return;
    }

    const i = keyCode === KeyCode.DOWN ? 1 : -1;
    let selectedIndex = this.state.selectedIndex + i;
    if (selectedIndex < 0) {
      selectedIndex = 0;
    } else if (selectedIndex >= this.getFilteredData().length) {
      selectedIndex = this.getFilteredData().length - 1;
    }

    this.setState({ selectedIndex });
  };
}

export default AutocompleteField;
